import {
  Cell,
  ColumnDef,
  ColumnFiltersState,
  Row,
  RowData,
  RowPinningState,
  SortingState,
} from "@tanstack/react-table";
import clsx from "clsx";
import { Fragment, memo, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { FilterOperation, FilterVariant } from "../filter";
import Pagination from "../pagination";
import { ChipsCellData } from "./cells/chipsCell";
import { useTable } from "./hooks";
import TableHeader from "./tableHeader";
import TableRow from "./tableRow";
import SkeletonTable from "../skeleton/skeletonTable";

declare module "@tanstack/react-table" {
  interface TableMeta<TData> {
    focusedColumn: string | undefined;
    selectedColumns: string[];
    onColumnFocus: (column: ColumnDef<TData>, entered: boolean) => void;
    onColumnSelected: (column: ColumnDef<TData, unknown>) => void;

    enableChipAdd?: boolean;
    onChipAdd?: (row: TData) => void;
    onChipSelected?: (row: TData, chip: ChipsCellData) => void;

    actions?: RowAction[];
    onActionClicked?: (row: TData, action: string, index: number) => void;
    enableRowActions?: (row: TData, index: number) => boolean;
    enableRowAction?: (row: TData, action: string, index: number) => boolean;

    size?: "sm" | "lg";
    bordered?: boolean;
    striped?: boolean;
    classNames?: {
      table?: string;
      cell?: string | ((cell: Cell<TData, any>) => string | undefined);
      row?: string | ((row: Row<TData>) => string | undefined);
      actionCell?: string;
    };
  }

  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: FilterVariant;
    maxFilterNum?: number;
  }

  interface ColumnFilter {
    id: string;
    value: unknown;
    operation?: FilterOperation;
  }
}

export type RowAction = {
  type: string;
  label: React.ReactNode;
};
export type RowQueryRequest = {
  page: number;
  pageSize: number;
  sorting: SortingState;
  filters: ColumnFiltersState;
};

export type RowQueryResult<RowType> = {
  data: RowType[];
  pageCount: number;
  nextPageToken?: string;
};

export type RowQuery<RowType> = (
  request: RowQueryRequest,
  pageTokens?: Record<number, string | undefined>,
) => Promise<RowQueryResult<RowType>>;

export interface TableProps<RowType> {
  className?: string;
  classNames?: {
    table?: string;
    cell?: string | ((cell: Cell<RowType, any>) => string | undefined);
    row?: (row: Row<RowType>) => string | undefined;
    actionCell?: string;
  };

  columns: ColumnDef<RowType>[];
  data: RowType[] | RowQuery<RowType>;
  onQueryRequest?: (request: RowQueryRequest) => void;
  isLoading?: boolean;
  pageCount?: number;
  responsive?: boolean;
  bordered?: boolean;
  striped?: boolean;
  size?: "sm" | "lg";

  enablePaging?: boolean;
  defaultPageSize?: number;
  fullPagination?: boolean;
  hideTotalPage?: boolean;

  enableSorting?: boolean;
  defaultSorting?: SortingState;

  enableRowSelection?: boolean | ((row: Row<RowType>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<RowType>) => boolean);
  enableSubRowSelection?: boolean | ((row: Row<RowType>) => boolean);
  defaultRowSelection?: string[];
  rowSelection?: string[];
  onRowSelection?: (rows: string[]) => void;

  enableExpanding?: boolean;
  getRowCanExpand?: (row: Row<RowType>) => boolean;
  getSubRows?: (originalRow: RowType, index: number) => undefined | RowType[];
  getSubComponent?: (props: { row: RowType; index: number }) => React.ReactNode;

  enableColumnSelection?: boolean | ((column: ColumnDef<RowType>) => boolean);
  enableMultiColumnSelection?:
    | boolean
    | ((column: ColumnDef<RowType>) => boolean);
  defaultColumnSelection?: string[];
  columnSelection?: string[];
  onColumnSelection?: (column: string[]) => void;

  enableFilters?: boolean;
  fullFilters?: boolean;
  defaultFilters?: ColumnFiltersState;
  onFilter?: (filters: ColumnFiltersState) => void;

  getRowId?: (
    originalRow: RowType,
    index: number,
    parent?: Row<RowType>,
  ) => string;

  actions?: RowAction[];
  onActionClicked?: (row: RowType, action: string, index: number) => void;
  enableRowActions?: (row: RowType, index: number) => boolean;
  enableRowAction?: (row: RowType, action: string, index: number) => boolean;

  enableRowClick?: boolean | ((row: RowType) => boolean);
  onRowClick?: (row: RowType) => void;

  enableChipAdd?: boolean;
  onChipAdd?: (row: RowType) => void;
  onChipSelected?: (row: RowType, chip: ChipsCellData) => void;

  enableRowPinning?: boolean;
  defaultPinning?: RowPinningState;
  FullPageMaxHeight?: boolean;

  pageResetKey?: number;

  footer?: React.ReactNode;
}

function Table<RowType>(props: Readonly<TableProps<RowType>>) {
  const {
    responsive = false,
    size = "lg",
    enablePaging = false,
    fullPagination = true,
    hideTotalPage = false,
    fullFilters = false,
    footer,
    classNames,
    className,
    FullPageMaxHeight = false,
    isLoading,
    getSubComponent,
    onRowClick,
    enableRowClick,
    onFilter,
  } = props;

  const {
    setPageSize,
    nextPage,
    previousPage,
    setPageIndex,
    getPageCount,
    getHeaderGroups,
    getTopRows,
    getCenterRows,
    getBottomRows,
    getCanNextPage,
    getCanPreviousPage,
    getState,
    setColumnFilters,
  } = useTable(props);

  const { columnFilters } = getState();

  useEffect(() => {
    onFilter?.(columnFilters);
  }, [columnFilters, onFilter]);

  if (isLoading) {
    return <SkeletonTable columns={10} />;
  }

  const Row = (row: Row<RowType>) => (
    <TableRow
      {...row}
      className={
        typeof classNames?.row === "function"
          ? classNames.row(row)
          : classNames?.row
      }
      enableRowClick={enableRowClick}
      onRowClick={onRowClick}
    />
  );

  return (
    <div className={className}>
      <div
        className={clsx("overflow-x-auto", {
          "max-h-[calc(100vh-295px)] overflow-y-auto": FullPageMaxHeight,
        })}
      >
        <table
          className={twMerge(
            clsx("w-full overflow-scroll border-collapse", {
              "whitespace-nowrap": responsive,
              "mb-4": !!footer || (enablePaging && getPageCount()),
            }),
            classNames?.table,
          )}
        >
          <thead
            className={clsx({
              "sticky top-0 bg-white shadow-[0px_0px_0_2px_#DAE0E6]":
                FullPageMaxHeight,
            })}
          >
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeader
                    {...header}
                    filterState={getState().columnFilters.find(
                      (filter) => filter.id === header.id,
                    )}
                    setFilters={setColumnFilters}
                    fullFilters={fullFilters}
                    key={header.id}
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {getTopRows().map((row) => (
              <Fragment key={row.id}>
                <Row {...row} />
                {row.getCanExpand() &&
                  row.getIsExpanded() &&
                  !!getSubComponent && (
                    <tr>
                      <td
                        colSpan={row.getVisibleCells().length}
                        className="border border-gunmetal-100"
                      >
                        {getSubComponent({
                          row: row.original,
                          index: row.index,
                        })}
                      </td>
                    </tr>
                  )}
              </Fragment>
            ))}
            {getCenterRows().map((row) => (
              <Fragment key={row.id}>
                <Row {...row} />
                {row.getCanExpand() &&
                  row.getIsExpanded() &&
                  !!getSubComponent && (
                    <tr>
                      <td
                        colSpan={row.getVisibleCells().length}
                        className="border border-gunmetal-100"
                      >
                        {getSubComponent({
                          row: row.original,
                          index: row.index,
                        })}
                      </td>
                    </tr>
                  )}
              </Fragment>
            ))}
            {getBottomRows().map((row) => (
              <Fragment key={row.id}>
                <Row {...row} />
                {row.getCanExpand() &&
                  row.getIsExpanded() &&
                  !!getSubComponent && (
                    <tr>
                      <td
                        colSpan={row.getVisibleCells().length}
                        className="border border-gunmetal-100"
                      >
                        {getSubComponent({
                          row: row.original,
                          index: row.index,
                        })}
                      </td>
                    </tr>
                  )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {(!!footer || (enablePaging && !!getPageCount())) && (
        <div className="flex flex-row gap-4">
          <div className="flex-1 items-center">{footer}</div>
          {enablePaging && getPageCount() >= 0 && (
            <Pagination
              fullNav={fullPagination}
              hideTotalPage={hideTotalPage}
              canNextPage={getCanNextPage()}
              canPreviousPage={getCanPreviousPage()}
              setPageSize={setPageSize}
              nextPage={nextPage}
              previousPage={previousPage}
              gotoPage={setPageIndex}
              size={size}
              pageCount={getPageCount()}
              pageIndex={getState().pagination.pageIndex}
              pageSize={getState().pagination.pageSize}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default memo(Table) as typeof Table;
